// components/layout/Footer.js
import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Container from '../common/Container';
import Button from '../common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTwitter, 
  faLinkedin, 
  faFacebook, 
  faInstagram 
} from '@fortawesome/free-brands-svg-icons';

const FooterWrapper = styled.footer`
  background: var(--background-secondary);
  padding: 4rem 0 2rem 0;
  color: var(--text-primary);
  width: 100%;

  @media (max-width: 576px) {
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FooterLogo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--accent-primary);
  margin-bottom: 1rem;
`;

const FooterDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const FooterTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const FooterLink = styled.a`
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: var(--accent-primary);
  }
`;

const StyledLink = styled(Link)`
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: var(--accent-primary);
  }
`;

const BottomBar = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 576px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const Footer = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleNavigation = (sectionId) => {
        if (location.pathname !== '/') {
            // If we're not on the home page, navigate to home first
            navigate('/');
            // Use setTimeout to ensure the navigation completes before trying to scroll
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        } else {
            // If we're already on the home page, just scroll
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    return (
        <FooterWrapper>
            <Container>
                <FooterGrid>
                    <FooterColumn>
                        <FooterLogo>
                            <a 
                                onClick={() => handleNavigation('home')} 
                                style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                            >
                                <img src="/signage-logo.png" alt="Signage Logo" style={{ height: '40px', width: 'auto' }} />
                            </a>
                        </FooterLogo>
                        <FooterDescription>
                            Signage proudly presents Sivo - the ultimate software for sign manufacturers.
                            We're dedicated to making your workflow easier, your numbers clearer, and your profit margins larger.
                        </FooterDescription>
                        <SocialLinks>
                            <FooterLink href="https://www.linkedin.com/company/signagellc/" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faLinkedin} /></FooterLink>
                            <FooterLink href="https://instagram.com/signagellc" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faInstagram} /></FooterLink>
                        </SocialLinks>
                    </FooterColumn>

                    <FooterColumn>
                        <FooterTitle>Company</FooterTitle>
                        <FooterLink href="#about">About Us</FooterLink>
                        <FooterLink href="#features">Features</FooterLink>
                        <FooterLink href="#pricing">Pricing</FooterLink>
                    </FooterColumn>

                    <FooterColumn>
                        <FooterTitle>Resources</FooterTitle>
                        <FooterLink href="#demo">Demo</FooterLink>
                        <FooterLink href="#support">Support</FooterLink>
                        <StyledLink to="/sponsor">Become a Sponsor</StyledLink>
                    </FooterColumn>

                    <FooterColumn>
                        <FooterTitle>Contact</FooterTitle>
                        <FooterLink href="mailto:support@signage-llc.com">support@signage-llc.com</FooterLink>
                        <Button variant="secondary" fullWidth href="#contact">
                            Contact Us
                        </Button>
                    </FooterColumn>
                </FooterGrid>

                <BottomBar>
                    <div>© 2025 Signage LLC. All rights reserved.</div>
                    <div>
                        <StyledLink to="/privacy-policy" style={{ marginRight: '1rem' }}>
                            Privacy Policy
                        </StyledLink>
                        <StyledLink to="/terms-of-service" style={{ marginRight: '1rem' }}>
                            Terms of Service
                        </StyledLink>
                        <StyledLink to="/software-license-agreement" style={{ marginRight: '1rem' }}>
                            Software Agreement
                        </StyledLink>
                        <StyledLink to="/refund-policy">
                            Refund Policy
                        </StyledLink>
                    </div>
                </BottomBar>
            </Container>
        </FooterWrapper>
    );
};

export default Footer;
